/**
 * Prompt Version Service — wraps /api/admin/ai/isa/prompts*
 * Phase 4.7 — WQ-166
 */

export interface PromptVersion {
  id: string;
  agent_id: string;
  version: number;
  variant_name: string | null;
  prompt_text: string;
  prompt_hash: string;
  is_active: boolean;
  isa_quality_score: number | null;
  isa_sample_count: number;
  created_by: string;
  created_at: string;
  activated_at: string | null;
  deactivated_at: string | null;
}

type FetchFn = (url: string, options?: RequestInit) => Promise<Response>;

export async function createPrompt(authFetch: FetchFn, data: {
  agent_id: string; prompt_text: string; variant_name?: string;
}): Promise<PromptVersion> {
  const res = await authFetch('/api/admin/ai/isa/prompts', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error(`Failed to create prompt: ${res.status}`);
  return res.json();
}

export async function listPrompts(
  authFetch: FetchFn, agentId: string, variantName?: string
): Promise<PromptVersion[]> {
  const qs = variantName ? `?variant_name=${encodeURIComponent(variantName)}` : '';
  const res = await authFetch(`/api/admin/ai/isa/prompts/${agentId}${qs}`);
  if (!res.ok) throw new Error(`Failed to list prompts: ${res.status}`);
  return res.json();
}

export async function getActivePrompt(
  authFetch: FetchFn, agentId: string, variantName?: string
): Promise<PromptVersion> {
  const qs = variantName ? `?variant_name=${encodeURIComponent(variantName)}` : '';
  const res = await authFetch(`/api/admin/ai/isa/prompts/${agentId}/active${qs}`);
  if (!res.ok) throw new Error(`Failed to get active prompt: ${res.status}`);
  return res.json();
}

export async function activatePrompt(
  authFetch: FetchFn, versionId: string
): Promise<PromptVersion> {
  const res = await authFetch(`/api/admin/ai/isa/prompts/${versionId}/activate`, {
    method: 'POST',
  });
  if (!res.ok) throw new Error(`Failed to activate prompt: ${res.status}`);
  return res.json();
}

export async function compareVariants(
  authFetch: FetchFn, agentId: string, variantA: string, variantB: string
): Promise<Record<string, unknown>> {
  const params = new URLSearchParams({
    agent_id: agentId,
    variant_a: variantA,
    variant_b: variantB,
  });
  const res = await authFetch(`/api/admin/ai/isa/prompts/compare?${params}`);
  if (!res.ok) throw new Error(`Failed to compare variants: ${res.status}`);
  return res.json();
}
